import React from 'react'
import {
  AttachmentLink,
  Button,
  Heading,
  KennoFile,
} from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { Section, TextTable } from '../../components'

interface FormValues {
  title: string
  description: string
  files: KennoFile[]
  email: string
  phoneNumber: string
}

interface FeedbackPreviewProps {
  formValues: FormValues
  isSubmitting: boolean
  submit: () => void
  cancel: () => void
}

export const FeedbackPreview: React.FC<FeedbackPreviewProps> = ({
  formValues,
  isSubmitting,
  submit,
  cancel,
}) => {
  const { t } = useTranslation(['common', 'feedback'])

  const { title, description, files, email, phoneNumber } = formValues

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    submit()
  }

  return (
    <>
      <div className="bg-brand-1 py-8 rounded-t-lg -mt-7 -mx-7 mb-10 text-center">
        <Heading size="l" element="h1">
          {t('feedback:headings.preview')}
        </Heading>
      </div>
      <Section className="mb-12">
        <TextTable className="table-fixed w-full min-tablet:text-lg">
          <TextTable.Row>
            <TextTable.Cell>{t('feedback:labels.email')}</TextTable.Cell>
            <TextTable.Cell>{email}</TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t('feedback:labels.phoneNumber')}</TextTable.Cell>
            <TextTable.Cell>{phoneNumber}</TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t('feedback:labels.title')}</TextTable.Cell>
            <TextTable.Cell className="whitespace-pre-wrap break-words">
              {title}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t('feedback:labels.description')}</TextTable.Cell>
            <TextTable.Cell className="whitespace-pre-wrap break-words">
              {description}
            </TextTable.Cell>
          </TextTable.Row>
          {files && files.length > 0 && (
            <TextTable.Row>
              <TextTable.Cell>{t('feedback:labels.files')}</TextTable.Cell>
              <TextTable.Cell>
                <div className="space-y-2">
                  {files.map((file) => (
                    <AttachmentLink
                      key={file.uuid || file.fileName}
                      href={file._objectUrl}
                      text={file.metadata?.originalFileName ?? file.fileName}
                      icon={file.fileType === 'image' ? 'image' : 'file'}
                      target="_blank"
                      className="mb-3 last:mb-0 break-all"
                    />
                  ))}
                </div>
              </TextTable.Cell>
            </TextTable.Row>
          )}
        </TextTable>
      </Section>

      <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-end">
        <Button
          variant="secondary"
          onClick={handleCancel}
          className="order-2 sm:order-1"
          disabled={isSubmitting}
        >
          {t('feedback:actions.edit')}
        </Button>
        <Button
          onClick={handleSubmit}
          className="order-1 sm:order-2"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {t('feedback:actions.submit')}
        </Button>
      </div>
    </>
  )
}
